import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { translations, type Language, type TranslationKey } from './translations';

interface LanguageContextValue {
  lang: Language;
  dir: 'rtl' | 'ltr';
  t: (key: TranslationKey) => string;
  switchLang: (lang: Language) => void;
  localePath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const SUPPORTED_LANGS: Language[] = ['he', 'ar', 'en'];

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang: paramLang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const lang: Language = SUPPORTED_LANGS.includes(paramLang as Language)
    ? (paramLang as Language)
    : 'he';

  const dir = lang === 'en' ? 'ltr' : 'rtl';

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);
  }, [dir, lang]);

  const t = useMemo(() => {
    return (key: TranslationKey): string => {
      const entry = translations[key];
      return entry ? entry[lang] : key;
    };
  }, [lang]);

  const switchLang = (newLang: Language) => {
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(he|ar|en)/, '') || '/';
    navigate(`/${newLang}${pathWithoutLang}`);
  };

  const localePath = (path: string) => `/${lang}${path}`;

  return (
    <LanguageContext.Provider value={{ lang, dir, t, switchLang, localePath }}>
      {children}
    </LanguageContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
